import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import { api } from '../../lib/api'
import { bus } from '../../App'
import type { StoredPrompt, PromptType } from '../../../shared/types/context'
import { TextContentViewer } from '../TextContentViewer'
import { ClickToCopy } from '../ClickToCopy'
import { ToggleButton } from '../ToggleButton'
import { ContextMenu } from '../ContextMenu'
import type { ContextMenuItem } from '../ContextMenu'
import { TagEditor } from '../TagEditor'
import { useSelectionStore } from '../../state/selectionStore'

type ComposerPromptType = PromptType | ''

const DROP_DATA_KEY = 'application/prompt-hash'
const HASH_SEPARATOR = '\n\n'
const COMPOSER_STORAGE_KEY = 'prompt-composer-state'
const PROMPT_HASH_PATTERN = /^[0-9a-f]{64}$/i

export function PromptComposerPanel() {
  const [prompts, setPrompts] = useState<StoredPrompt[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPromptHash, setSelectedPromptHash] = useState<string | null>(null)
  const [composition, setComposition] = useState<string[]>([])
  const [composeHash, setComposeHash] = useState<string | null>(null)
  const [composeType, setComposeType] = useState<ComposerPromptType>('')
  const [composeError, setComposeError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [typeMutationPending, setTypeMutationPending] = useState(false)
  const [activeDropIndex, setActiveDropIndex] = useState<number | null>(null)
  const [hoveredChipIndex, setHoveredChipIndex] = useState<number | null>(null)
  const [selectedChipIndex, setSelectedChipIndex] = useState<number | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [originalWasComposite, setOriginalWasComposite] = useState(false)
  const [isNewMode, setIsNewMode] = useState(false)
  const [promptTags, setPromptTags] = useState<Record<number, string[]>>({})
  const showTaggedOnly = useSelectionStore((state) => state.prompts_tagged)
  const setSelection = useSelectionStore((state) => state.set)
  const [contextMenuOpen, setContextMenuOpen] = useState(false)
  const [contextMenuPromptHash, setContextMenuPromptHash] = useState<string | null>(null)
  const [contextMenuTargetEl, setContextMenuTargetEl] = useState<HTMLElement | null>(null)
  const contextMenuAnchorRef = useRef<HTMLDivElement | null>(null)
  const [tagEditorPromptHash, setTagEditorPromptHash] = useState<string | null>(null)
  const [tagEditorAnchorEl, setTagEditorAnchorEl] = useState<HTMLElement | null>(null)

  const mountedRef = useRef(false)
  const draggingSourceRef = useRef<'library' | 'composer' | null>(null)
  const draggingIndexRef = useRef<number | null>(null)
  const dropHandledRef = useRef(false)
  const storageHydratedRef = useRef(false)
  const previewContainerRef = useRef<HTMLDivElement | null>(null)
  const previewSegmentRefs = useRef<Array<HTMLDivElement | null>>([])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const promptMap = useMemo(() => {
    const map: Record<string, StoredPrompt> = {}
    for (const prompt of prompts) {
      map[prompt.hash] = prompt
    }
    return map
  }, [prompts])

  const selectedPrompt = selectedPromptHash ? promptMap[selectedPromptHash] ?? null : null

  const loadPrompts = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false
      if (!silent) {
        setLoading(true)
        setError(null)
      }
      try {
        const list = await api().services.context.listPrompts()
        if (mountedRef.current) {
          setPrompts(list ?? [])
        }
      } catch (err) {
        console.error('[PromptComposerPanel] Failed to load prompts:', err)
        if (!silent && mountedRef.current) {
          setError('Failed to load prompt library')
        }
      } finally {
        if (!silent && mountedRef.current) {
          setLoading(false)
        }
      }
    },
    [],
  )

  useEffect(() => {
    void loadPrompts()
  }, [loadPrompts])

  const hydrateMissingPrompts = useCallback(
    async (hashes: string[]) => {
      const missing = hashes.filter((hash) => !promptMap[hash])
      if (!missing.length) {
        return
      }

      try {
        const fetched = await Promise.all(
          missing.map(async (hash) => {
            try {
              return await api().services.context.getPrompt(hash)
            } catch (err) {
              console.error(`[PromptComposerPanel] Failed to load prompt ${hash}:`, err)
              return null
            }
          }),
        )

        setPrompts((prev) => {
          const next = [...prev]
          let mutated = false

          for (const prompt of fetched) {
            if (!prompt) {
              continue
            }
            const index = next.findIndex((candidate) => candidate.hash === prompt.hash)
            if (index === -1) {
              next.push(prompt)
              mutated = true
            } else {
              next[index] = prompt
              mutated = true
            }
          }

          if (!mutated) {
            return prev
          }

          next.sort((a, b) => {
            const aTimestamp = a.last_used_at ?? a.created_at
            const bTimestamp = b.last_used_at ?? b.created_at
            return bTimestamp - aTimestamp
          })
          return next
        })
      } catch (err) {
      console.error('[PromptComposerPanel] Failed to hydrate prompts:', err)
    }
  },
  [promptMap],
)

  const hydrateMissingPromptsRef = useRef(hydrateMissingPrompts)

  useEffect(() => {
    hydrateMissingPromptsRef.current = hydrateMissingPrompts
  }, [hydrateMissingPrompts])

  const refreshPromptTags = useCallback(async () => {
    try {
      const tagged = await api().services.tags.listEntitiesWithTags('prompt')
      if (!mountedRef.current) {
        return
      }
      if (!Array.isArray(tagged)) {
        setPromptTags({})
        return
      }
      const map: Record<number, string[]> = {}
      for (const row of tagged) {
        map[row.entityId] = Array.isArray(row.tags) ? row.tags : []
      }
      setPromptTags(map)
    } catch (err) {
      console.error('[PromptComposerPanel] Failed to load prompt tags:', err)
      if (mountedRef.current) {
        setPromptTags({})
      }
    }
  }, [])

  useEffect(() => {
    void refreshPromptTags()
  }, [refreshPromptTags])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      const stored = window.localStorage.getItem(COMPOSER_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as {
          composition?: unknown
          selectedPromptHash?: unknown
          composeType?: unknown
          selectedChipIndex?: unknown
        }

        if (Array.isArray(parsed.composition)) {
          const normalized = parsed.composition.filter(
            (value): value is string => typeof value === 'string' && value.length > 0,
          )
          setComposition(normalized)
          if (normalized.length) {
            void hydrateMissingPromptsRef.current(normalized)
          }
        }

        const storedPromptHash =
          typeof parsed.selectedPromptHash === 'string' && parsed.selectedPromptHash.length > 0
            ? parsed.selectedPromptHash
            : null

        if (storedPromptHash) {
          setSelectedPromptHash(storedPromptHash)
          setSelectedChipIndex(null)
          setHoveredChipIndex(null)
          void hydrateMissingPromptsRef.current([storedPromptHash])
        } else if (parsed.selectedPromptHash === null) {
          setSelectedPromptHash(null)
        }

        if (
          typeof parsed.composeType === 'string' &&
          (parsed.composeType === '' ||
            parsed.composeType === 'system' ||
            parsed.composeType === 'user' ||
            parsed.composeType === 'task')
        ) {
          setComposeType(parsed.composeType as ComposerPromptType)
        }

        const storedChipIndex =
          typeof parsed.selectedChipIndex === 'number' && Number.isFinite(parsed.selectedChipIndex)
            ? Math.floor(parsed.selectedChipIndex)
            : null

        if (!storedPromptHash && parsed.selectedChipIndex === null) {
          setSelectedChipIndex(null)
        } else if (!storedPromptHash && storedChipIndex !== null) {
          setSelectedChipIndex(storedChipIndex)
        }
      }
    } catch (err) {
      console.error('[PromptComposerPanel] Failed to load composer state:', err)
    } finally {
      storageHydratedRef.current = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const computeHash = async () => {
      if (!composition.length) {
        if (!cancelled) {
          setComposeHash(null)
        }
        return
      }

      const contents = composition.map((hash) => promptMap[hash]?.content ?? '')
      if (contents.some((item) => !item)) {
        // Content missing, wait for hydration
        if (!cancelled) {
          setComposeHash(null)
        }
        return
      }

      const joined = contents.join(HASH_SEPARATOR)
      try {
        const encoder = new TextEncoder()
        const buffer = encoder.encode(joined)
        const digest = await crypto.subtle.digest('SHA-256', buffer)
        const hex = Array.from(new Uint8Array(digest))
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('')
        if (!cancelled) {
          setComposeHash(hex)
        }
      } catch (err) {
        console.error('[PromptComposerPanel] Failed to compute composed hash:', err)
        if (!cancelled) {
          setComposeHash(null)
        }
      }
    }

    void computeHash()

    return () => {
      cancelled = true
    }
  }, [composition, promptMap])

  useEffect(() => {
    if (selectedPrompt) {
      setComposeType(selectedPrompt.type ?? '')
    }
  }, [selectedPrompt?.hash, selectedPrompt?.type])

  useEffect(() => {
    previewSegmentRefs.current = previewSegmentRefs.current.slice(0, composition.length)

    setSelectedChipIndex((prev) => {
      if (prev === null) {
        return null
      }
      if (!composition.length) {
        return null
      }
      if (prev >= composition.length) {
        return composition.length - 1
      }
      return prev
    })

    setHoveredChipIndex((prev) => {
      if (prev === null) {
        return null
      }
      if (!composition.length) {
        return null
      }
      if (prev >= composition.length) {
        return null
      }
      return prev
    })
  }, [composition.length])

  useEffect(() => {
    const container = previewContainerRef.current
    if (!container) {
      return
    }

    const isLibrary = selectedPromptHash !== null

    if (selectedChipIndex === null || isLibrary) {
      if (container.scrollTop !== 0) {
        container.scrollTo({ top: 0, behavior: 'smooth' })
      }
      return
    }

    const segment = previewSegmentRefs.current[selectedChipIndex]
    if (!segment) {
      return
    }

    window.requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect()
      const segmentRect = segment.getBoundingClientRect()
      const offset = segmentRect.top - containerRect.top + container.scrollTop
      container.scrollTo({ top: offset, behavior: 'smooth' })
    })
  }, [selectedChipIndex, composition.length, selectedPromptHash])

  useEffect(() => {
    if (selectedChipIndex === null) {
      return
    }

    const composerList = document.querySelector('.prompt-composer__list') as HTMLElement | null
    const chips = composerList?.querySelectorAll<HTMLDivElement>('.prompt-composer__chip:not(.prompt-library__chip)')
    if (chips && chips[selectedChipIndex]) {
      chips[selectedChipIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [selectedChipIndex, composition.length])

  useEffect(() => {
    if (!mountedRef.current || typeof window === 'undefined' || !storageHydratedRef.current) {
      return
    }

    try {
      const snapshot = {
        composition,
        selectedPromptHash,
        composeType,
        selectedChipIndex,
      }
      window.localStorage.setItem(COMPOSER_STORAGE_KEY, JSON.stringify(snapshot))
    } catch (err) {
      console.error('[PromptComposerPanel] Failed to persist composer state:', err)
    }
  }, [composition, selectedPromptHash, composeType, selectedChipIndex])

  useEffect(() => {
    const handleTagEvent = async (_event: string, payload?: { entityType?: string }) => {
      if (payload?.entityType === 'prompt') {
        await refreshPromptTags()
      }
    }
    const unsubscribeAssign = bus.on('tags/assign', handleTagEvent)
    const unsubscribeRemove = bus.on('tags/remove', handleTagEvent)
    const unsubscribeReset = bus.on('tags/reset', async () => {
      await refreshPromptTags()
    })

    return () => {
      unsubscribeAssign()
      unsubscribeRemove()
      unsubscribeReset()
    }
  }, [refreshPromptTags])

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.isComposing) {
      return
    }
    const topPrompt = filteredPrompts[0]
    if (!topPrompt) {
      return
    }
    event.preventDefault()
    setComposition((prev) => {
      const next = [...prev, topPrompt.hash]
      setSelectedChipIndex(next.length - 1)
      return next
    })
    setSearchTerm('')
    setSelectedPromptHash(null)
    setHoveredChipIndex(null)
    setComposeError(null)
  }

  const filteredPrompts = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase()
    const candidates = showTaggedOnly
      ? prompts.filter((prompt) => {
          if (!prompt.tag_entity_id) {
            return false
          }
          const tags = promptTags[prompt.tag_entity_id] ?? []
          return tags.length > 0
        })
      : prompts

    if (!normalized) {
      return candidates
    }
    if (PROMPT_HASH_PATTERN.test(normalized)) {
      const directMatch = candidates.find((prompt) => prompt.hash.toLowerCase() === normalized)
      return directMatch ? [directMatch] : []
    }
    return candidates.filter((prompt) => {
      const content = prompt.content.toLowerCase()
      const tagValues =
        prompt.tag_entity_id && promptTags[prompt.tag_entity_id]
          ? promptTags[prompt.tag_entity_id].map((tag) => tag.toLowerCase())
          : []
      return (
        content.includes(normalized) ||
        prompt.hash.toLowerCase().includes(normalized) ||
        (prompt.type ?? '').toLowerCase().includes(normalized) ||
        tagValues.some((tag) => tag.includes(normalized))
      )
    })
  }, [prompts, promptTags, searchTerm, showTaggedOnly])

  useEffect(() => {
    const normalized = searchTerm.trim().toLowerCase()
    if (!PROMPT_HASH_PATTERN.test(normalized)) {
      return
    }
    const alreadyLoaded = prompts.some((prompt) => prompt.hash === normalized)
    if (alreadyLoaded) {
      return
    }
    void hydrateMissingPrompts([normalized])
  }, [searchTerm, prompts, hydrateMissingPrompts])

  const promptTagLookup = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const prompt of prompts) {
      if (!prompt.tag_entity_id) {
        map[prompt.hash] = []
        continue
      }
      map[prompt.hash] = promptTags[prompt.tag_entity_id] ?? []
    }
    return map
  }, [prompts, promptTags])

  const tagEditorEntityId = tagEditorPromptHash
    ? promptMap[tagEditorPromptHash]?.tag_entity_id ?? null
    : null

  const handleClearComposition = () => {
    setComposition([])
    setComposeError(null)
    setSelectedChipIndex(null)
    setHoveredChipIndex(null)
    setIsEditMode(false)
    setOriginalWasComposite(false)
    setIsNewMode(false)
  }

  const handleOpenPrompt = useCallback(
    async (prompt: StoredPrompt) => {
      setComposeType(prompt.type ?? '')
      setComposeError(null)
      setSelectedPromptHash(null)
      setIsEditMode(false)
      setIsNewMode(false)

      if (prompt.is_composed) {
        try {
          const components = await api().services.context.decomposePrompt(prompt.content)
          await hydrateMissingPrompts(components)
          setComposition(components)
          setSelectedChipIndex(0)
          setHoveredChipIndex(null)
          setOriginalWasComposite(true)
        } catch (err) {
          console.error('[PromptComposerPanel] Failed to decompose prompt:', err)
          setComposeError('Unable to load composition for this prompt')
          setComposition([])
          setSelectedChipIndex(null)
          setHoveredChipIndex(null)
          setSelectedPromptHash(prompt.hash)
          setOriginalWasComposite(false)
        }
      } else {
        await hydrateMissingPrompts([prompt.hash])
        setComposition((prev) => {
          const newComposition = [...prev, prompt.hash]
          setSelectedChipIndex(newComposition.length - 1)
          return newComposition
        })
        setHoveredChipIndex(null)
        setOriginalWasComposite(false)
      }
    },
    [hydrateMissingPrompts],
  )

  const handleTypeChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as ComposerPromptType
    setComposeType(value)

    if (!selectedPrompt) {
      return
    }

    setTypeMutationPending(true)
    try {
      const normalized = value === '' ? null : value
      await api().services.context.setPromptType(selectedPrompt.hash, normalized)
      setPrompts((prev) =>
        prev.map((prompt) =>
          prompt.hash === selectedPrompt.hash
            ? {
                ...prompt,
                type: normalized,
              }
            : prompt,
        ),
      )
    } catch (err) {
      console.error('[PromptComposerPanel] Failed to update prompt type:', err)
    } finally {
      setTypeMutationPending(false)
    }
  }

  const handleCompose = async () => {
    if (!composition.length) {
      setComposeError('Add prompts to the composition before composing')
      return
    }
    const missing = composition.filter((hash) => !promptMap[hash])
    if (missing.length) {
      setComposeError('One or more prompts are still loading')
      return
    }

    setIsSaving(true)
    setComposeError(null)
    try {
      const normalizedType = composeType === '' ? null : composeType
      const composedHash = await api().services.context.composePrompt(composition, {
        separator: HASH_SEPARATOR,
        type: normalizedType,
      })
      await hydrateMissingPrompts([composedHash])
      setComposition([composedHash])
      setSelectedChipIndex(0)
      setHoveredChipIndex(null)
      setSelectedPromptHash(null)
      setComposeType(normalizedType ?? '')
      setIsEditMode(false)
      setOriginalWasComposite(false)
    } catch (err) {
      console.error('[PromptComposerPanel] Failed to compose prompt:', err)
      setComposeError('Failed to compose the prompts')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSplitPrompt = async () => {
    if (composition.length !== 1) {
      setComposeError('Split only works with exactly one prompt in the composition')
      return
    }

    const hash = composition[0]
    const prompt = promptMap[hash]
    if (!prompt) {
      setComposeError('Selected prompt not found')
      return
    }

    setIsSaving(true)
    setComposeError(null)
    try {
      // Split the content by blank lines (one or more whitespace-only lines)
      const sections = prompt.content.split(/\n\s*\n/).filter((section) => section.trim())

      if (sections.length < 2) {
        setComposeError('Prompt must have at least 2 sections separated by blank lines')
        setIsSaving(false)
        return
      }

      // Create new prompts for each section
      const newHashes: string[] = []
      for (const section of sections) {
        const newHash = await api().services.context.createPrompt(section, { type: prompt.type })
        newHashes.push(newHash)
      }

      // Hydrate the new prompts into the promptMap
      await hydrateMissingPrompts(newHashes)
      setComposition(newHashes)
      setSelectedChipIndex(0)
      setHoveredChipIndex(null)
      setSelectedPromptHash(null)
      setIsEditMode(false)
      setOriginalWasComposite(prompt.is_composed)
    } catch (err) {
      console.error('[PromptComposerPanel] Failed to split prompt:', err)
      setComposeError('Failed to split the prompt')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDecomposePrompt = async () => {
    if (composition.length !== 1) {
      setComposeError('Decompose only works with exactly one prompt in the composition')
      return
    }

    const hash = composition[0]
    const prompt = promptMap[hash]
    if (!prompt || !prompt.is_composed) {
      setComposeError('Selected prompt is not a composite prompt')
      return
    }

    setIsSaving(true)
    setComposeError(null)
    try {
      const components = await api().services.context.decomposePrompt(prompt.content)
      await hydrateMissingPrompts(components)
      setComposition(components)
      setSelectedChipIndex(0)
      setHoveredChipIndex(null)
      setSelectedPromptHash(null)
      setOriginalWasComposite(true)
    } catch (err) {
      console.error('[PromptComposerPanel] Failed to decompose prompt:', err)
      setComposeError('Failed to decompose the prompt')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveEdit = async (newContent: string) => {
    setIsSaving(true)
    setComposeError(null)
    try {
      // Split by blank lines and create new prompts for each section
      const sections = newContent.split(/\n\s*\n/).filter((section) => section.trim())

      if (sections.length === 0) {
        setComposeError('Content cannot be empty')
        setIsSaving(false)
        return
      }

      const normalizedType = composeType === '' ? null : composeType

      if (sections.length === 1) {
        // Single section - save as a single prompt
        const newHash = await api().services.context.createPrompt(sections[0], { type: normalizedType })
        await hydrateMissingPrompts([newHash])
        setComposition([newHash])
        setSelectedChipIndex(0)
        setHoveredChipIndex(null)
        setIsEditMode(false)
        setIsNewMode(false)
        setOriginalWasComposite(false)
      } else {
        // Multiple sections - create prompts for each section, then compose them
        const newHashes: string[] = []

        for (const section of sections) {
          const newHash = await api().services.context.createPrompt(section, { type: normalizedType })
          newHashes.push(newHash)
        }

        await hydrateMissingPrompts(newHashes)

        // Now compose them into a single composite prompt
        const composedHash = await api().services.context.composePrompt(newHashes, {
          separator: HASH_SEPARATOR,
          type: normalizedType,
        })

        await hydrateMissingPrompts([composedHash])
        setComposition([composedHash])
        setSelectedChipIndex(0)
        setHoveredChipIndex(null)
        setIsEditMode(false)
        setIsNewMode(false)
        setOriginalWasComposite(true)
      }
    } catch (err) {
      console.error('[PromptComposerPanel] Failed to save edited prompt:', err)
      setComposeError('Failed to save the edited prompt')
    } finally {
      setIsSaving(false)
    }
  }

  const handleNewPrompt = () => {
    setIsNewMode(true)
    setIsEditMode(false)
    setSelectedPromptHash(null)
    setSelectedChipIndex(null)
    setHoveredChipIndex(null)
    setComposeError(null)
  }

  const handleSaveNew = async (newContent: string) => {
    setIsSaving(true)
    setComposeError(null)
    try {
      if (!newContent.trim()) {
        setComposeError('Content cannot be empty')
        setIsSaving(false)
        return
      }

      const normalizedType = composeType === '' ? null : composeType
      const newHash = await api().services.context.createPrompt(newContent, { type: normalizedType })
      await hydrateMissingPrompts([newHash])

      setComposition((prev) => {
        const newComposition = [...prev, newHash]
        setSelectedChipIndex(newComposition.length - 1)
        return newComposition
      })
      setHoveredChipIndex(null)
      setIsNewMode(false)
    } catch (err) {
      console.error('[PromptComposerPanel] Failed to save new prompt:', err)
      setComposeError('Failed to save the new prompt')
    } finally {
      setIsSaving(false)
    }
  }

  const handleLibraryDragStart = (hash: string) => (event: DragEvent<HTMLElement>) => {
    draggingSourceRef.current = 'library'
    draggingIndexRef.current = null
    dropHandledRef.current = false
    event.dataTransfer.setData(DROP_DATA_KEY, hash)
    event.dataTransfer.setData('text/plain', hash)
    event.dataTransfer.effectAllowed = 'copy'
    setSelectedPromptHash(hash)
    setSelectedChipIndex(null)
    setHoveredChipIndex(null)
    setComposeError(null)
  }

  const handleLibraryDragEnd = () => {
    draggingSourceRef.current = null
    draggingIndexRef.current = null
    dropHandledRef.current = false
    setActiveDropIndex(null)
  }

  const handleLibraryContextMenu = (event: MouseEvent<HTMLButtonElement>, hash: string) => {
    event.preventDefault()
    event.stopPropagation()
    setContextMenuPromptHash(hash)
    setContextMenuTargetEl(event.currentTarget)
    if (contextMenuAnchorRef.current) {
      contextMenuAnchorRef.current.style.left = `${event.clientX}px`
      contextMenuAnchorRef.current.style.top = `${event.clientY}px`
    }
    setContextMenuOpen(true)
  }

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuOpen(false)
    setContextMenuPromptHash(null)
    setContextMenuTargetEl(null)
  }, [])

  const openPromptTagEditor = useCallback(
    (hash: string, anchorEl: HTMLElement | null) => {
      const prompt = promptMap[hash]
      if (!prompt?.tag_entity_id) {
        console.warn('[PromptComposerPanel] Prompt missing tag_entity_id', hash)
        return
      }
      setTagEditorPromptHash(hash)
      setTagEditorAnchorEl(anchorEl)
    },
    [promptMap],
  )

  const closePromptTagEditor = useCallback(() => {
    setTagEditorPromptHash(null)
    setTagEditorAnchorEl(null)
  }, [])

  const contextMenuItems = useMemo<ContextMenuItem[]>(() => {
    if (!contextMenuPromptHash) {
      return []
    }
    return [
      {
        label: 'Tags',
        value: 'tags',
        onClick: () => {
          openPromptTagEditor(contextMenuPromptHash, contextMenuTargetEl ?? contextMenuAnchorRef.current)
          handleCloseContextMenu()
        },
      },
    ]
  }, [contextMenuPromptHash, contextMenuTargetEl, openPromptTagEditor, handleCloseContextMenu, contextMenuAnchorRef])

  const handleComposerDragStart = (index: number) => (event: DragEvent<HTMLDivElement>) => {
    draggingSourceRef.current = 'composer'
    draggingIndexRef.current = index
    dropHandledRef.current = false
    const hash = composition[index]
    if (hash) {
      event.dataTransfer.setData(DROP_DATA_KEY, hash)
      event.dataTransfer.setData('text/plain', hash)
      event.dataTransfer.effectAllowed = 'move'
    }
  }

  const handleComposerDragEnd = () => {
    if (draggingSourceRef.current === 'composer' && draggingIndexRef.current !== null && !dropHandledRef.current) {
      const indexToRemove = draggingIndexRef.current
      setComposition((prev) => prev.filter((_, index) => index !== indexToRemove))
      setSelectedChipIndex((prevSelected) => {
        if (prevSelected === null) {
          return null
        }
        if (prevSelected === indexToRemove) {
          return null
        }
        if (prevSelected > indexToRemove) {
          return prevSelected - 1
        }
        return prevSelected
      })
      setHoveredChipIndex((prevHovered) => {
        if (prevHovered === null) {
          return null
        }
        if (prevHovered === indexToRemove) {
          return null
        }
        if (prevHovered > indexToRemove) {
          return prevHovered - 1
        }
        return prevHovered
      })
    }
    draggingSourceRef.current = null
    draggingIndexRef.current = null
    dropHandledRef.current = false
    setActiveDropIndex(null)
  }

  const allowDrop = (event: DragEvent<HTMLElement>) => {
    const types = Array.from(event.dataTransfer.types)
    if (types.includes(DROP_DATA_KEY)) {
      event.preventDefault()
      event.dataTransfer.dropEffect = draggingSourceRef.current === 'composer' ? 'move' : 'copy'
    }
  }

  const findInsertionIndex = (event: DragEvent<HTMLElement>) => {
    const container = event.currentTarget
    const chips = Array.from(container.querySelectorAll<HTMLDivElement>('.prompt-composer__chip'))
    const { clientY } = event

    for (let index = 0; index < chips.length; index += 1) {
      const rect = chips[index].getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2
      if (clientY < midpoint) {
        return index
      }
    }

    return chips.length
  }

  const handleComposerListDragOver = (event: DragEvent<HTMLDivElement>) => {
    allowDrop(event)
    const index = findInsertionIndex(event)
    setActiveDropIndex(index)
  }

  const handleComposerListDragLeave = (event: DragEvent<HTMLDivElement>) => {
    const related = event.relatedTarget as Node | null
    if (related && event.currentTarget.contains(related)) {
      return
    }
    setActiveDropIndex(null)
  }

  const handleComposerListDrop = (event: DragEvent<HTMLDivElement>) => {
    allowDrop(event)
    event.preventDefault()
    event.stopPropagation()
    const rawData = event.dataTransfer.getData(DROP_DATA_KEY) || event.dataTransfer.getData('text/plain')
    const hash = rawData.trim()
    if (!hash) {
      return
    }

    dropHandledRef.current = true
    const targetIndex = findInsertionIndex(event)
    setActiveDropIndex(null)

    if (draggingSourceRef.current === 'library') {
      setComposition((prev) => {
        const next = [...prev]
        next.splice(targetIndex, 0, hash)
        return next
      })
      setSelectedPromptHash(null)
      setSelectedChipIndex(targetIndex)
      setHoveredChipIndex(null)
      setComposeError(null)
      return
    }

    if (draggingSourceRef.current === 'composer') {
      const fromIndex = draggingIndexRef.current
      if (fromIndex === null) {
        return
      }

      if (targetIndex === fromIndex || targetIndex === fromIndex + 1) {
        return
      }

      const insertIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex

      setComposition((prev) => {
        if (fromIndex < 0 || fromIndex >= prev.length) {
          return prev
        }

        const next = [...prev]
        const [moved] = next.splice(fromIndex, 1)
        if (!moved) {
          return prev
        }

        next.splice(insertIndex, 0, moved)
        return next
      })
      setSelectedChipIndex(insertIndex)
      setHoveredChipIndex((prevHovered) => {
        if (prevHovered === null) {
          return null
        }
        if (prevHovered === fromIndex) {
          return insertIndex
        }
        if (fromIndex < insertIndex) {
          if (prevHovered > fromIndex && prevHovered <= insertIndex) {
            return prevHovered - 1
          }
          return prevHovered
        }
        if (fromIndex > insertIndex) {
          if (prevHovered >= insertIndex && prevHovered < fromIndex) {
            return prevHovered + 1
          }
          return prevHovered
        }
        return prevHovered
      })
    }
  }

  const handleComposerListMouseLeave = () => {
    setHoveredChipIndex(null)
  }

  const handleChipMouseEnter = (index: number) => () => {
    setHoveredChipIndex(index)
  }

  const handleChipMouseLeave = (index: number) => () => {
    setHoveredChipIndex((prev) => (prev === index ? null : prev))
  }

  const handleChipClick = (index: number) => () => {
    setSelectedChipIndex(index)
    setSelectedPromptHash(null)
  }

  const compositionSegments = useMemo(
    () => composition.map((hash) => promptMap[hash] ?? null),
    [composition, promptMap],
  )

  const isLibrarySelection = selectedPromptHash !== null

  const previewSegments = isLibrarySelection
    ? [
        {
          key: selectedPromptHash ?? 'library-selection',
          content: selectedPrompt?.content ?? 'Loading prompt content…',
          hovered: false,
          selected: false,
          segmentIndex: null,
        },
      ]
    : composition.map((hash, index) => {
        const prompt = compositionSegments[index]
        return {
          key: `${hash}-${index}`,
          content: prompt?.content ?? 'Loading prompt content…',
          hovered: hoveredChipIndex === index,
          selected: selectedChipIndex === index,
          segmentIndex: index,
        }
      })

  const previewCountLabel = isLibrarySelection ? 'Library prompt' : `${composition.length} prompts`

  const composedContent = useMemo(() => {
    if (isNewMode) {
      return ''
    }
    if (isLibrarySelection) {
      return selectedPrompt?.content ?? ''
    }
    return composition
      .map((hash) => promptMap[hash]?.content ?? '')
      .filter((content) => content.length > 0)
      .join(HASH_SEPARATOR)
  }, [composition, promptMap, isLibrarySelection, selectedPrompt, isNewMode])

  const renderPromptPreview = (prompt: StoredPrompt) => {
    const firstLine = prompt.content.trim().split('\n')[0] ?? ''
    const label = firstLine.length > 120 ? `${firstLine.slice(0, 117)}…` : firstLine
    return label || '(empty prompt)'
  }

  const showFullEditorView = isEditMode

  if (loading) {
    return (
      <div className="panel-shell">
        <div className="panel-content panel-content--horizontal">
          <div className="empty-state">
            <p>Loading prompts…</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="panel-shell">
        <div className="panel-content panel-content--horizontal">
          <div className="empty-state">
            <p>{error}</p>
            <button type="button" className="btn btn-positive" onClick={() => void loadPrompts()}>
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="panel-shell">
      <div className="panel-controls">
        <ToggleButton
          label="tagged"
          enabled={showTaggedOnly}
          onToggle={(value) => {
            setSelection({ prompts_tagged: value })
          }}
        />
      </div>
      <div className="panel-content prompt-composer-panel">
        <section className="prompt-library">
          <header className="prompt-library__header">
            <input
              type="search"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search prompts…"
              className="prompt-library__search"
            />
            <div className="prompt-library__summary">
              <span>Total: {prompts.length}</span>
              <span>Filtered: {filteredPrompts.length}</span>
            </div>
          </header>
          {filteredPrompts.length === 0 ? (
            <div className="prompt-library__empty">No prompts match the current search</div>
          ) : (
            <ul className="prompt-library__list">
              {filteredPrompts.map((prompt) => {
                const chipClasses = ['prompt-composer__chip', 'prompt-library__chip']
                if (selectedPromptHash === prompt.hash) {
                  chipClasses.push('prompt-composer__chip--selected')
                }
                const promptTagList = promptTagLookup[prompt.hash] ?? []
                const primaryTag = promptTagList[0]

                return (
                  <li key={prompt.hash}>
                    <button
                      type="button"
                      className={chipClasses.join(' ')}
                      draggable
                      onDragStart={handleLibraryDragStart(prompt.hash)}
                      onDragEnd={handleLibraryDragEnd}
                      onClick={() => {
                        setSelectedPromptHash(prompt.hash)
                        setSelectedChipIndex(null)
                        setHoveredChipIndex(null)
                        setComposeError(null)
                      }}
                      onDoubleClick={() => void handleOpenPrompt(prompt)}
                      onContextMenu={(event) => handleLibraryContextMenu(event, prompt.hash)}
                    >
                      <span className="prompt-library__title">{renderPromptPreview(prompt)}</span>
                      <div className="prompt-library__meta">
                        <span className="prompt-library__meta-text">
                          {prompt.type ?? '—'}
                          {prompt.is_composed ? ' · composite' : ''}
                        </span>
                        {primaryTag && (
                          <button
                            type="button"
                            className="tag-badge tag-badge--assigned tag-badge--interactive prompt-library__tag"
                            onClick={(event) => {
                              event.stopPropagation()
                              if (prompt.tag_entity_id) {
                                openPromptTagEditor(prompt.hash, event.currentTarget)
                              }
                            }}
                          >
                            {primaryTag}
                          </button>
                        )}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
        <section className="prompt-composer">
          <header className="prompt-composer__header">
            <div className="prompt-composer__settings">
              <label className="prompt-composer__setting">
                <span className="prompt-composer__setting-label">Type</span>
                <select
                  value={composeType}
                  onChange={handleTypeChange}
                  className="prompt-composer__type-select"
                  disabled={typeMutationPending}
                >
                  <option value="">Unset</option>
                  <option value="system">System</option>
                  <option value="user">User</option>
                  <option value="task">Task</option>
                </select>
              </label>
              <div className="prompt-composer__setting">
                <span className="prompt-composer__setting-label">Hash</span>
                {composeHash ? (
                  <ClickToCopy
                    value={composeHash}
                    className="prompt-composer__hash"
                    title="Click to copy hash"
                    ariaLabel="Copy hash to clipboard"
                  />
                ) : (
                  <code className="prompt-composer__hash">—</code>
                )}
              </div>
            </div>
            <div className="prompt-composer__controls">
              <button type="button" className="btn" onClick={handleNewPrompt} disabled={isSaving || typeMutationPending}>
                new
              </button>
              <button type="button" className="btn" onClick={handleClearComposition}>
                clear
              </button>
              {composition.length === 1 && promptMap[composition[0]]?.is_composed ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => void handleDecomposePrompt()}
                  disabled={isSaving || typeMutationPending}
                  title="Decompose the composite prompt into its component prompts"
                >
                  {isSaving ? 'Decomposing…' : 'Decompose'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn"
                  onClick={() => void handleCompose()}
                  disabled={isSaving || composition.length === 0 || typeMutationPending}
                >
                  {isSaving ? 'Composing…' : 'Compose'}
                </button>
              )}
              <button
                type="button"
                className="btn"
                onClick={() => void handleSplitPrompt()}
                disabled={isSaving || composition.length !== 1 || typeMutationPending}
                title="Split the single prompt into sections separated by blank lines"
              >
                {isSaving ? 'Splitting…' : 'Split'}
              </button>
            </div>
          </header>
          <div className={`prompt-composer__body${showFullEditorView ? ' prompt-composer__body--preview-only' : ''}`}>
            <div
              className={`prompt-composer__list${showFullEditorView ? ' prompt-composer__list--hidden' : ''}`}
              onDragOver={handleComposerListDragOver}
              onDragLeave={handleComposerListDragLeave}
              onDrop={handleComposerListDrop}
              onMouseLeave={handleComposerListMouseLeave}
            >
              {composition.length === 0 ? (
                <div className="prompt-composer__empty">Drag prompts here to start composing</div>
              ) : (
                composition.map((hash, index) => {
                  const prompt = promptMap[hash]
                  const chipClasses = ['prompt-composer__chip']

                  if (activeDropIndex === index) {
                    chipClasses.push('prompt-composer__chip--drop-before')
                  }
                  if (activeDropIndex === index + 1) {
                    chipClasses.push('prompt-composer__chip--drop-after')
                  }
                  if (hoveredChipIndex === index) {
                    chipClasses.push('prompt-composer__chip--hovered')
                  }
                  if (selectedChipIndex === index) {
                    chipClasses.push('prompt-composer__chip--selected')
                  }

                  const promptTagList = prompt ? promptTagLookup[prompt.hash] ?? [] : []
                  const primaryTag = promptTagList[0]

                  return (
                    <div
                      key={`${hash}-${index}`}
                      className={chipClasses.join(' ')}
                      draggable
                      onDragStart={handleComposerDragStart(index)}
                      onDragEnd={handleComposerDragEnd}
                      onMouseEnter={handleChipMouseEnter(index)}
                      onMouseLeave={handleChipMouseLeave(index)}
                      onClick={handleChipClick(index)}
                    >
                      <span className="prompt-composer__chip-title">
                        {prompt ? renderPromptPreview(prompt) : hash}
                      </span>
                      <div className="prompt-composer__chip-meta">
                        {primaryTag && (
                          <button
                            type="button"
                            className="tag-badge tag-badge--assigned tag-badge--interactive prompt-composer__chip-tag"
                            onClick={(event) => {
                              event.stopPropagation()
                              if (prompt?.tag_entity_id) {
                                openPromptTagEditor(hash, event.currentTarget)
                              }
                            }}
                          >
                            {primaryTag}
                          </button>
                        )}
                        <span className="prompt-composer__chip-meta-text">{prompt?.type ?? '—'}</span>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            <div className={`prompt-composer__preview${showFullEditorView ? ' prompt-composer__preview--full' : ''}`}>
              <header className="prompt-composer__preview-header">
                <span className="prompt-composer__preview-title">{isNewMode ? 'New' : isEditMode ? 'Edit' : 'Preview'}</span>
                <div className="prompt-composer__preview-actions">
                  {!isNewMode && <span className="prompt-composer__preview-count">{previewCountLabel}</span>}
                  {composition.length > 0 && !isLibrarySelection && !isNewMode && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setIsEditMode(!isEditMode)}
                      disabled={isSaving}
                    >
                      {isEditMode ? 'Preview' : 'Edit'}
                    </button>
                  )}
                </div>
              </header>
              <div className="prompt-composer__preview-content" ref={previewContainerRef}>
                {isNewMode ? (
                  <TextContentViewer
                    content=""
                    onSave={handleSaveNew}
                    language="plaintext"
                    readOnly={false}
                    initiallyEditing={true}
                    enableWordWrap={true}
                  />
                ) : isEditMode ? (
                  <TextContentViewer
                    content={composedContent}
                    onSave={handleSaveEdit}
                    language="plaintext"
                    readOnly={false}
                    initiallyEditing={true}
                    enableWordWrap={true}
                  />
                ) : previewSegments.length === 0 ? (
                  <div className="prompt-composer__preview-empty">
                    Prompt content will appear here as you compose
                  </div>
                ) : (
                  previewSegments.map((segment) => {
                    const segmentClasses = ['prompt-composer__preview-segment']

                    if (!isLibrarySelection && segment.hovered) {
                      segmentClasses.push('prompt-composer__preview-segment--hovered')
                    }
                    if (!isLibrarySelection && segment.selected) {
                      segmentClasses.push('prompt-composer__preview-segment--selected')
                    }

                    return (
                      <div
                        key={segment.key}
                        ref={(element) => {
                          if (!isLibrarySelection && segment.segmentIndex !== null) {
                            previewSegmentRefs.current[segment.segmentIndex] = element
                          }
                        }}
                        className={segmentClasses.join(' ')}
                      >
                        <pre className="prompt-composer__preview-text">{segment.content}</pre>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>
          {composeError ? <div className="prompt-composer__error">{composeError}</div> : null}
        </section>
      </div>
      {tagEditorPromptHash && tagEditorAnchorEl && tagEditorEntityId !== null && (
        <TagEditor
          entityType="prompt"
          entityId={tagEditorEntityId}
          onClose={closePromptTagEditor}
          anchorEl={tagEditorAnchorEl}
        />
      )}
      <div
        ref={contextMenuAnchorRef}
        style={{ position: 'fixed', width: '1px', height: '1px', pointerEvents: 'none', visibility: 'hidden' }}
      />
      <ContextMenu
        items={contextMenuItems}
        isOpen={contextMenuOpen}
        onClose={handleCloseContextMenu}
        anchorEl={contextMenuAnchorRef.current}
        placement="bottom-left"
      />
    </div>
  )
}
